import { scoreLine, getWordScore } from './scoring.js';

const LETTERS = Object.freeze([...new Set('ABCÇDEFGĞHIİJKLMNOÖPRSŞTUÜVYZ')]);
const patternCache = new WeakMap();
const planningIndexCache = new WeakMap();

function planningIndex(dictionary) {
  if (planningIndexCache.has(dictionary)) return planningIndexCache.get(dictionary);
  const byLength = new Map();
  for (const word of dictionary) {
    if (typeof word !== 'string' || word.length < 2 || word.length > 10) continue;
    const letters = [...word.toLocaleUpperCase('tr-TR')];
    if (letters.length !== word.length || letters.some(letter => !LETTERS.includes(letter))) continue;
    if (!byLength.has(letters.length)) byLength.set(letters.length, { words: [], at: Array.from({ length: letters.length }, () => new Map()) });
    const group = byLength.get(letters.length);
    const index = group.words.length;
    group.words.push(letters);
    for (let position = 0; position < letters.length; position++) {
      if (!group.at[position].has(letters[position])) group.at[position].set(letters[position], []);
      group.at[position].get(letters[position]).push(index);
    }
  }
  planningIndexCache.set(dictionary, byLength);
  return byLength;
}

// An unfinished word is a feasible target only if every committed letter in
// its row/column window agrees with an actual dictionary word. Record which
// next letters preserve the longest target, for both axes and every board size.
function longWordPlans(board, dictionary) {
  const size = board.length;
  const plans = Array.from({ length: size }, () => Array.from({ length: size }, () => new Map()));
  const indexed = planningIndex(dictionary);
  for (let length = size; length >= 2; length--) {
    const group = indexed.get(length);
    if (!group) continue;
    for (let axis = 0; axis < 2; axis++) {
      for (let line = 0; line < size; line++) {
        for (let start = 0; start <= size - length; start++) {
          const fixed = [];
          const empty = [];
          for (let offset = 0; offset < length; offset++) {
            const row = axis ? start + offset : line;
            const col = axis ? line : start + offset;
            const letter = board[row][col];
            if (letter === '') empty.push({ row, col, offset });
            else fixed.push({ offset, letter: letter.toLocaleUpperCase('tr-TR') });
          }
          if (!empty.length) continue;
          const progress = fixed.length + 1;
          const remember = (row, col, letter) => {
            const prior = plans[row][col].get(letter);
            if (!prior || length > prior.length || (length === prior.length && progress > prior.progress)) {
              plans[row][col].set(letter, { length, progress });
            }
          };
          if (!fixed.length) {
            for (const { row, col, offset } of empty) {
              for (const letter of group.at[offset].keys()) remember(row, col, letter);
            }
            continue;
          }
          const anchor = fixed.map(({ offset, letter }) => group.at[offset].get(letter) || []);
          const shortest = anchor.reduce((best, list) => list.length < best.length ? list : best);
          for (const index of shortest) {
            const word = group.words[index];
            if (fixed.some(({ offset, letter }) => word[offset] !== letter)) continue;
            for (const { row, col, offset } of empty) {
              remember(row, col, word[offset]);
            }
          }
        }
      }
    }
  }
  return plans;
}

function oneMovePatterns(dictionary) {
  if (patternCache.has(dictionary)) return patternCache.get(dictionary);
  const patterns = new Map();
  for (const word of dictionary) {
    if (typeof word !== 'string' || word.length < 2 || word.length > 10) continue;
    const normalizedWord = word.toLocaleLowerCase('tr-TR');
    for (let i = 0; i < word.length; i++) {
      const pattern = `${normalizedWord.slice(0, i)}*${normalizedWord.slice(i + 1)}`;
      patterns.set(pattern, getWordScore(word));
    }
  }
  patternCache.set(dictionary, patterns);
  return patterns;
}

function nextMovePotential(board, patterns) {
  const n = board.length;
  let best = 0;
  for (let axis = 0; axis < 2; axis++) {
    for (let line = 0; line < n; line++) {
      const cells = axis ? board.map(row => row[line]) : board[line];
      for (let length = 2; length <= n; length++) {
        for (let start = 0; start <= n - length; start++) {
          const slice = cells.slice(start, start + length);
          if (slice.filter(letter => letter === '').length !== 1) continue;
          const pattern = slice.map(letter => letter === '' ? '*' : letter.toLocaleLowerCase('tr-TR')).join('');
          best = Math.max(best, patterns.get(pattern) || 0);
        }
      }
    }
  }
  return best;
}

function potentialCreatedAt(board, patterns, row, col, letter) {
  let best = 0;
  const size = board.length;
  for (let axis = 0; axis < 2; axis++) {
    const position = axis ? row : col;
    const line = axis ? col : row;
    for (let length = 2; length <= size; length++) {
      for (let start = Math.max(0, position - length + 1); start <= Math.min(position, size - length); start++) {
        const slice = Array.from({ length }, (_, offset) => {
          const at = start + offset;
          return at === position ? letter : axis ? board[at][line] : board[line][at];
        });
        if (slice.filter(value => value === '').length !== 1) continue;
        const pattern = slice.map(value => value === '' ? '*' : value.toLocaleLowerCase('tr-TR')).join('');
        best = Math.max(best, patterns.get(pattern) || 0);
      }
    }
  }
  return best;
}

function lineScore(cells, dictionary) {
  return scoreLine(cells, dictionary).reduce((sum, word) => sum + word.score, 0);
}

function baselineLineScores(board, dictionary) {
  return {
    rows: board.map(row => lineScore(row, dictionary)),
    cols: board.map((_, col) => lineScore(board.map(row => row[col]), dictionary)),
  };
}

// Only the candidate's row and column can change their canonical word scores.
function candidateGain(board, dictionary, baseline, row, col, letter) {
  const nextRow = [...board[row]];
  nextRow[col] = letter;
  const nextCol = board.map((cells, r) => r === row ? letter : cells[col]);
  return lineScore(nextRow, dictionary) - baseline.rows[row] +
    lineScore(nextCol, dictionary) - baseline.cols[col];
}

function positionPreference(board, row, col) {
  const neighbors = [[row - 1, col], [row + 1, col], [row, col - 1], [row, col + 1]];
  const occupiedNeighbors = neighbors.filter(([r, c]) => board[r]?.[c] && board[r][c] !== '').length;
  const center = (board.length - 1) / 2;
  return occupiedNeighbors * board.length * 2 - Math.abs(row - center) - Math.abs(col - center);
}

function chooseBotPlacement({ board, dictionary, incomingLetter = null, letterChoices = LETTERS, difficulty = 4, planningDictionary = dictionary, selectionFraction = null, opponentBoard = null, random = Math.random }) {
  if (!Array.isArray(board) || ![5, 7, 9, 10].includes(board.length) ||
      board.some(row => !Array.isArray(row) || row.length !== board.length) || !(dictionary instanceof Set)) {
    throw new TypeError('Bot için geçerli tahta ve maç sözlüğü gerekir.');
  }
  const letters = incomingLetter == null ? letterChoices : [incomingLetter];
  const baseline = baselineLineScores(board, dictionary);
  const patterns = oneMovePatterns(dictionary);
  const baselinePotential = nextMovePotential(board, patterns);
  if (!Number.isInteger(difficulty) || difficulty < 1 || difficulty > 5) throw new RangeError('Geçersiz bot zorluğu.');
  const wordPlans = longWordPlans(board, planningDictionary);
  // LAB candidate: equal information; never use the hidden opponent board.
  opponentBoard = null;
  const opponentThreat = new Map();
  if (difficulty === 5 && incomingLetter === null && opponentBoard) {
    if (!Array.isArray(opponentBoard) || opponentBoard.length !== board.length ||
        opponentBoard.some(row => !Array.isArray(row) || row.length !== board.length)) {
      throw new TypeError('Rakip tahtası aynı boyutta olmalı.');
    }
    const opponentBaseline = baselineLineScores(opponentBoard, dictionary);
    const baselineOpponentPotential = nextMovePotential(opponentBoard, patterns);
    for (const letter of new Set(letters)) {
      if (typeof letter !== 'string' || !/^[ABCÇDEFGĞHIİJKLMNOÖPRSŞTUÜVYZ]$/iu.test(letter)) continue;
      const normalized = letter.toLocaleUpperCase('tr-TR');
      let gain = 0;
      let futureRisk = 0;
      for (let row = 0; row < board.length; row++) {
        for (let col = 0; col < board.length; col++) {
          if (opponentBoard[row][col] === '') {
            gain = Math.max(gain, candidateGain(opponentBoard, dictionary, opponentBaseline, row, col, normalized));
            futureRisk = Math.max(futureRisk, potentialCreatedAt(opponentBoard, patterns, row, col, normalized) - baselineOpponentPotential);
          }
        }
      }
      opponentThreat.set(normalized, gain * 4 + futureRisk * 3);
    }
  }
  const candidates = [];
  for (let row = 0; row < board.length; row++) {
    for (let col = 0; col < board.length; col++) {
      if (board[row][col] !== '') continue;
      for (const letter of letters) {
        if (typeof letter !== 'string' || !/^[ABCÇDEFGĞHIİJKLMNOÖPRSŞTUÜVYZ]$/iu.test(letter)) continue;
        const normalized = letter.toLocaleUpperCase('tr-TR');
        const next = { row, col, letter: normalized, scoreGain: candidateGain(board, dictionary, baseline, row, col, normalized) };
        const futureGain = Math.max(0, potentialCreatedAt(board, patterns, row, col, normalized) - baselinePotential);
        const value = next.scoreGain * 10 + futureGain * 2;
        candidates.push({ move: next, value, plan: wordPlans[row][col].get(normalized) || { length: 0, progress: 0 }, threat: opponentThreat.get(normalized) || 0,
          position: positionPreference(board, row, col) });
      }
    }
  }
  if (!candidates.length) return null;
  // At the final cell no future own-board potential remains.
  if (difficulty === 5 && board.flat().filter(cell => cell === "").length === 1) {
    candidates.sort((a, b) => b.move.scoreGain - a.move.scoreGain || b.position - a.position);
    return candidates[0].move;
  }
  candidates.sort((a, b) => b.plan.length - a.plan.length || b.plan.progress - a.plan.progress || b.value - a.value ||
    (difficulty === 5 ? a.threat - b.threat : 0) || b.position - a.position);
  if (difficulty >= 4) return candidates[0].move;
  // On large boards, the old top-65% easy pool was mostly aimless, while
  // limiting medium to the best long-word plan made it nearly as strong as hard.
  // Mix the two existing legal choice pools per move to create an intermediate
  // level without altering the scorer, dictionary, or hidden-board rules.
  const roll = Number(random());
  const probability = Number.isFinite(roll) ? Math.min(0.999999999, Math.max(0, roll)) : 0;
  let spread;
  if (board.length > 5 && difficulty === 1) {
    spread = Math.max(1, Math.ceil(candidates.length * (probability < 0.45 ? 0.1 : 0.65)));
  } else if (board.length > 5 && difficulty === 3 && probability < 0.45) {
    const bestPlan = candidates[0].plan;
    const matchingPlanCount = candidates.findIndex(candidate =>
      candidate.plan.length !== bestPlan.length || candidate.plan.progress !== bestPlan.progress);
    spread = Math.min(Math.max(1, Math.ceil(candidates.length * 0.1)), board.length * 2,
      matchingPlanCount < 0 ? candidates.length : matchingPlanCount);
  } else {
    spread = Math.max(1, Math.ceil(candidates.length * ({ 1: 0.65, 2: 0.3, 3: 0.1 }[difficulty])));
  }
  const selection = board.length > 5 ? Number(random()) : probability;
  if(selectionFraction!==null)spread=Math.max(1,Math.ceil(candidates.length*selectionFraction));
  const index = Math.min(spread - 1, Math.floor((Number.isFinite(selection) ? Math.max(0, selection) : 0) * spread));
  return candidates[index].move;
}

export { LETTERS, chooseBotPlacement };
